// @system — Promote the preloaded brand-font stylesheet(s) to real stylesheets.
//
// index.html (built by webpack from brand.json via scripts/lib/brand-fonts.cjs) ships the Google
// Fonts CSS as `<link rel="preload" as="style" data-brand-fonts>` so it never blocks first paint.
// This runs at app start and flips those links to `rel="stylesheet"`; the CSS is already in the
// HTTP cache by then, so the swap costs no extra round trip. No-JS visitors get the <noscript> copy.

/**
 * @param {Document | null} [doc]
 * @returns {number} number of links activated
 */
export function activateBrandFonts(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return 0
  const links = doc.querySelectorAll('link[rel="preload"][as="style"][data-brand-fonts]')
  let n = 0
  for (const link of links) {
    link.setAttribute('rel', 'stylesheet')
    link.removeAttribute('as')
    n += 1
  }
  return n
}

/**
 * Activate the brand fonts once the first frame has been painted, never before it.
 *
 * The fonts are `display=optional`: the page keeps whatever font its first paint used, which is
 * what keeps the Lighthouse LCP inside budget (a webfont on the LCP path costs ~0.4 s) and the text
 * from re-laying out. That only holds when the @font-face rules arrive AFTER the first paint. Since
 * index.html holds the first paint until the consent banner is parsed (the `expect` link in head),
 * the deferred app bundle could run before that paint, declare the fonts early and swap the
 * server-rendered text to the webfont a frame later (CLS 0.07 on /pricing, layout-shift source the
 * pricing paragraph). requestAnimationFrame runs only once rendering is unblocked; the timeout
 * after it runs after that frame is painted.
 * @param {Document | null} [doc]
 * @param {{ requestAnimationFrame?: (cb: () => void) => unknown, setTimeout?: (cb: () => void, ms?: number) => unknown } | null} [win]
 * @returns {void}
 */
export function activateBrandFontsAfterFirstPaint(
  doc = typeof document !== 'undefined' ? document : null,
  win = typeof window !== 'undefined' ? window : null,
) {
  if (!doc) return
  if (!win || typeof win.requestAnimationFrame !== 'function' || typeof win.setTimeout !== 'function') {
    activateBrandFonts(doc)
    return
  }
  const later = /** @type {(cb: () => void, ms?: number) => unknown} */ (win.setTimeout.bind(win))
  win.requestAnimationFrame(() => { later(() => { activateBrandFonts(doc) }, 0) })
}
